import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { ThemeProvider } from "@mui/material/styles";
import "../styles/ChecklistCard.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";

/** Calm sage green for informational prompt tips icons. */
const CALM_INFO_ICON_COLOR = "#5f8f73";

/** Ordered list of supported protective tones (values match backend `clauseTone`). */
export const DEFAULT_PROTECTIVE_TONE_OPTIONS = [
  "Buyer Protective",
  "Seller Protective",
  "Landlord Protective",
  "Tenant Protective",
];

const PROTECTIVE_TONE_LABELS = {
  "Buyer Protective": "Buyer Protective",
  "Seller Protective": "Seller Protective",
  "Landlord Protective": "Landlord Protective",
  "Tenant Protective": "Tenant Protective",
};

/** Shown below Copy after text is generated (same substance as prior header disclaimer). */
const LEGAL_DISCLAIMER = (
  <>
    <b>Legal Disclaimer: </b>
    These clauses are AI-generated for informational purposes only and do not
    constitute legal advice. Output is modeled on standard Ontario clause
    patterns, and may contain errors or omissions. Any clause should be reviewed
    and approved by your client&apos;s lawyer before execution. The Transaction
    Guide and its contributors disclaim all liability for use of generated
    content.
  </>
);

/** Shown when Type is Clause (operative provisions, not conditions). */
const CLAUSE_DETAILS_PROMPT_TIPS = [
  'Use common terms, if applicable, such as: "[PARTY] represents and warrants", "[PARTY] agrees to", "[PARTY] acknowledges"...etc.',
  'For clauses that involve work to be completed, include a consequence if not completed in time. Example: "Lawyer will holdback $5,000"',
  'Choose "Balanced" when you want to be neutral and even-handed between the parties.',
];

/** Shown when Type is Clause and Tone is Listing Agreement (OREA listing / brokerage–owner). */
const LISTING_AGREEMENT_DETAILS_PROMPT_TIPS = [
  "Describe the listing scenario in plain terms (e.g., remuneration, marketing, MLS, schedule changes).",
  "Output follows OREA Form 271/272 schedule style: formal wording, Seller or Landlord and Listing Brokerage with capitals, [PLACEHOLDERS] for missing details.",
  "Clause generation rules are looser for Listing Agreements than for other sections to allow for more flexibility in the wording, so additional editing may be required.",
];

/** Shown when Type is Condition (exit / waiver mechanics, not direct promises). */
const CONDITION_DETAILS_PROMPT_TIPS = [
  'State what the offer is conditional upon in plain terms (e.g., financing approval, home inspection, lawyer review) so the draft can follow the standard "This Offer is conditional upon..." structure.',
  'Insert a specific date for the condition deadline. Example: "Buyer home inspection by May 30th"',
  "Match Tone to who the condition favours: Buyer, Seller, Landlord or Tenant Protective.",
];

export default function ONClauseGenerator({
  APS,
  ListingType,
  SellerRep,
  BuyerRep,
  theme,
  /** Subset of {@link DEFAULT_PROTECTIVE_TONE_OPTIONS} for the Tone dropdown (Condition and Clause). */
  protectiveToneOptions = DEFAULT_PROTECTIVE_TONE_OPTIONS,
  /** When Type is Clause, include Balanced in the Tone dropdown. */
  showBalancedToneOption = true,
}) {
  const normalizedProtectiveToneOptions = useMemo(() => {
    const raw =
      Array.isArray(protectiveToneOptions) && protectiveToneOptions.length > 0
        ? protectiveToneOptions
        : DEFAULT_PROTECTIVE_TONE_OPTIONS;
    const seen = new Set();
    const deduped = raw.filter(
      (tone) =>
        DEFAULT_PROTECTIVE_TONE_OPTIONS.includes(tone) &&
        !seen.has(tone) &&
        seen.add(tone),
    );
    return deduped.length > 0 ? deduped : [...DEFAULT_PROTECTIVE_TONE_OPTIONS];
  }, [protectiveToneOptions]);

  const [clauseType, setClauseType] = useState("Condition");
  const [clauseFacts, setClauseFacts] = useState("");
  const [clauseTone, setClauseTone] = useState(
    () => normalizedProtectiveToneOptions[0] || "Buyer Protective",
  );

  const prevClauseTypeRef = useRef(clauseType);

  /** When Type changes, set tone in layout so MUI never paints Clause with a stale protective tone. */
  useLayoutEffect(() => {
    if (prevClauseTypeRef.current === clauseType) {
      return;
    }
    prevClauseTypeRef.current = clauseType;
    if (clauseType === "Clause" && showBalancedToneOption) {
      setClauseTone("Balanced");
    } else {
      setClauseTone(normalizedProtectiveToneOptions[0] || "Buyer Protective");
    }
  }, [clauseType, showBalancedToneOption, normalizedProtectiveToneOptions]);

  useEffect(() => {
    setClauseTone((prev) => {
      const forCondition = normalizedProtectiveToneOptions;
      const forClause = [
        ...(showBalancedToneOption ? ["Balanced"] : []),
        "Listing Agreement",
        ...normalizedProtectiveToneOptions,
      ];
      const allowed = clauseType === "Clause" ? forClause : forCondition;

      if (allowed.includes(prev)) {
        return prev;
      }
      if (clauseType === "Clause" && showBalancedToneOption) {
        return "Balanced";
      }
      return normalizedProtectiveToneOptions[0] || "Buyer Protective";
    });
  }, [clauseType, normalizedProtectiveToneOptions, showBalancedToneOption]);
  const [generatedClause, setGeneratedClause] = useState("");
  const [isGeneratingClause, setIsGeneratingClause] = useState(false);
  const [clauseError, setClauseError] = useState("");
  const effectiveClauseTone = clauseTone;
  const detailsLabel =
    clauseType === "Condition"
      ? "Condition Details"
      : clauseTone === "Listing Agreement"
        ? "Listing Agreement Details"
        : "Clause Details";
  const generateButtonLabel =
    clauseType === "Condition"
      ? "Generate Condition"
      : clauseTone === "Listing Agreement"
        ? "Generate Listing Agreement Clause"
        : "Generate Clause";

  const generateClauseWithClaude = async (e) => {
    e.preventDefault();
    setClauseError("");
    setGeneratedClause("");

    if (!clauseFacts.trim()) {
      setClauseError(
        "Please add transaction facts before generating a clause.",
      );
      return;
    }

    setIsGeneratingClause(true);

    try {
      const endpoint = "/.netlify/functions/generate-clause";

      const payload = {
        aps: APS,
        listingType: ListingType,
        sellerRep: SellerRep,
        buyerRep: BuyerRep,
        clauseType,
        clauseTone: effectiveClauseTone,
        clauseFacts,
      };

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let backendError = "";
        try {
          const errorData = await response.json();
          backendError = errorData?.error || "";
        } catch (parseError) {
          backendError = "";
        }

        if (response.status === 429) {
          setClauseError("Too many requests. Please wait a minute and retry.");
          return;
        }
        if (response.status === 504) {
          setClauseError("Clause generation timed out. Please try again.");
          return;
        }
        if (backendError) {
          setClauseError(
            `Clause generator error (${response.status}): ${backendError}`,
          );
          return;
        }
        throw new Error(`Generate endpoint error: ${response.status}`);
      }

      const data = await response.json();
      const clauseText = data?.clause || "";

      if (!clauseText.trim()) {
        throw new Error("No clause text returned.");
      }

      setGeneratedClause(clauseText.trim());
    } catch (error) {
      setClauseError(
        "Unable to generate clause right now. Verify backend env/config and retry.",
      );
    } finally {
      setIsGeneratingClause(false);
    }
  };

  return (
    <div className="checklist-card checklist-card-modern">
      <div className="flex flex-wrap checklist-card-header">
        <div>Ontario Condition/Clause Generator</div>
      </div>
      <div className="clause-card-content">
        <div className="pl-4 pr-4 pb-4">
          <div className="pb-3">
            Enter the details of the clause/condition you want to generate. Any
            custom clause should be carefully reviewed by your client's lawyer
            before use. If an applicable OREA clause exists, it should be used
            instead.
          </div>
          <div className="text-sm pt-2 mt-2 pb-2">
            <ul className="list-disc pl-2 m-0 space-y-1.5">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon
                  icon={faCircleInfo}
                  size="lg"
                  style={{ color: CALM_INFO_ICON_COLOR }}
                />{" "}
                <span>
                  {clauseType === "Clause" && clauseTone === "Listing Agreement"
                    ? "Tips for your listing agreement prompt"
                    : clauseType === "Clause"
                      ? "Tips for your clause prompt"
                      : "Tips for your condition prompt"}
                </span>
              </div>
              <ul className="list-disc pl-5 m-0">
                {(clauseType === "Clause" && clauseTone === "Listing Agreement"
                  ? LISTING_AGREEMENT_DETAILS_PROMPT_TIPS
                  : clauseType === "Clause"
                    ? CLAUSE_DETAILS_PROMPT_TIPS
                    : CONDITION_DETAILS_PROMPT_TIPS
                ).map((tip) => (
                  <li key={tip}>{tip}</li>
                ))}
              </ul>
            </ul>
          </div>
        </div>
        <form
          onSubmit={generateClauseWithClaude}
          className="flex flex-col gap-3 pl-4 pr-4 pb-4"
        >
          <ThemeProvider theme={theme}>
            <FormControl size="small" sx={{ maxWidth: 300 }}>
              <InputLabel className="select-default-label">Type</InputLabel>
              <Select
                value={clauseType}
                label="Type"
                onChange={(e) => setClauseType(e.target.value)}
                sx={{ borderRadius: 20 }}
              >
                <MenuItem value="Condition">Condition</MenuItem>
                <MenuItem value="Clause">Clause</MenuItem>
              </Select>
            </FormControl>
          </ThemeProvider>
          <ThemeProvider theme={theme}>
            <FormControl size="small" sx={{ maxWidth: 300 }}>
              <InputLabel className="select-default-label">Tone</InputLabel>
              <Select
                value={effectiveClauseTone}
                label="Tone"
                onChange={(e) => setClauseTone(e.target.value)}
                sx={{ borderRadius: 20 }}
              >
                {clauseType === "Clause" && showBalancedToneOption ? (
                  <MenuItem value="Balanced">Balanced</MenuItem>
                ) : null}
                {clauseType === "Clause" ? (
                  <MenuItem value="Listing Agreement">
                    Listing Agreement
                  </MenuItem>
                ) : null}
                {normalizedProtectiveToneOptions.map((toneValue) => (
                  <MenuItem key={toneValue} value={toneValue}>
                    {PROTECTIVE_TONE_LABELS[toneValue] ?? toneValue}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </ThemeProvider>
          <TextField
            label={detailsLabel}
            value={clauseFacts}
            onChange={(e) => setClauseFacts(e.target.value)}
            multiline
            minRows={4}
            required
            placeholder="Example: Describe the Condition or Clause in plain terms..."
          />
          <div className="flex items-center gap-3">
            <Button
              type="submit"
              variant="contained"
              className="header-style-cta"
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontFamily: "Manrope, sans-serif",
                fontWeight: 600,
                "&.Mui-disabled": {
                  background: "#eef2ff !important",
                  backgroundImage: "none !important",
                  color: "#64748b",
                  border: "1px solid rgba(148, 163, 184, 0.5)",
                  boxShadow: "none",
                },
              }}
              disabled={isGeneratingClause}
            >
              {isGeneratingClause ? "Generating..." : generateButtonLabel}
            </Button>
            {isGeneratingClause ? <CircularProgress size={20} /> : null}
          </div>
        </form>
        {clauseError ? (
          <div className="text-red-600 text-sm pl-4 pr-4 pb-2">
            {clauseError}
          </div>
        ) : null}
        {generatedClause ? (
          <div className="pl-4 pr-4 pb-4">
            <div className="rounded border border-gray-300 bg-gray-50 p-3">
              <div className="whitespace-pre-wrap text-sm">
                {generatedClause}
              </div>
              <div className="pt-2">
                <Button
                  variant="outlined"
                  sx={{ borderRadius: 20, textTransform: "none" }}
                  onClick={() => navigator.clipboard.writeText(generatedClause)}
                >
                  Copy Clause
                </Button>
              </div>
              <div className="text-xs text-gray-600 pt-3 mt-3 border-t border-gray-200">
                {LEGAL_DISCLAIMER}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
